import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { 
  generateVideo, 
  checkVideoGenerationStatus, 
  getVideoCredits, 
  calculateEstimatedCredits as calculateApiCredits, 
  VideoGenerationParams, 
  VideoGenerationStatus 
} from '../../services/runwareVideoService';
import { 
  calculateVideoCredits, 
  getEstimatedCreditCost, 
  getCreditCostBreakdown, 
  hasEnoughCredits,
  getBaseModelCost,
  CreditCalculationParams
} from '../../services/videoCreditCalculationService';

// Define duration presets for common use cases
interface DurationPreset {
  name: string;
  seconds: number;
  description: string;
}

// Define video model presets
interface VideoModel {
  id: string;
  name: string;
  description: string;
  maxDuration: number;
  maxResolution: string;
  aspectRatio: string;
  inputTypes: string[];
  creditCost: number;
}

// Define format options
interface FormatOption {
  id: string;
  name: string;
  extension: string;
  quality: number;
  description: string;
}

// Log entry interface for debug panel
interface LogEntry {
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'success';
  message: string;
  data?: any;
}

const TextToVideoGenerator: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  
  // Core state
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoCredits, setVideoCredits] = useState<number>(0);
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  const [baseModelCost, setBaseModelCost] = useState<number>(0);
  const [enhancePrompt, setEnhancePrompt] = useState(true);
  const [videoDuration, setVideoDuration] = useState<number>(10); // Default 10 seconds
  const [selectedDurationPreset, setSelectedDurationPreset] = useState<string>('short');
  const [selectedModel, setSelectedModel] = useState<string>('klingai:5@3');
  const [selectedFormat, setSelectedFormat] = useState<string>('mp4-hd');
  const [generationProgress, setGenerationProgress] = useState<number>(0);
  const [taskUUID, setTaskUUID] = useState<string | null>(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<number | null>(null);
  const [developerMode, setDeveloperMode] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);
  
  // Duration presets (in seconds) - aligned with actual model limitations
  const durationPresets: DurationPreset[] = [
    { name: 'short', seconds: 5, description: 'Short clip (5 seconds) - Max for Kling models' },
    { name: 'standard', seconds: 8, description: 'Standard clip (8 seconds) - Max for Veo3 models' },
    { name: 'medium', seconds: 10, description: 'Medium clip (10 seconds) - Max for Haliou 2' }
  ];
  
  // Video models with accurate specifications based on official Runware documentation
  // Simplified to only include Hailuo 02 and Google Veo3 per request
  const videoModels: VideoModel[] = [
    { 
      id: 'hailuo:02@1', // AIR ID format estimate
      name: 'Hailuo 02', 
      description: 'MiniMax\'s advanced model designed for realistic motion, sharp detail, and advanced physics rendering. Ideal for complex choreography and fast-moving subjects.', 
      maxDuration: 10, // Maximum 10 seconds per Runware docs
      maxResolution: '720p', // Official Runware support
      aspectRatio: '16:9 (1280x720)', // Standard aspect ratio supported
      inputTypes: ['Text-to-Video', 'Image-to-Video'],
      creditCost: 4.0 // Based on official pricing + margin
    },
    { 
      id: 'google:veo3@1', // AIR ID format estimate
      name: 'Google Veo3', 
      description: 'Adds native audio generation (dialogue, ambience, sound effects) on top of cinematic visuals and improved prompt understanding.', 
      maxDuration: 8, // Maximum 8 seconds per Runware docs
      maxResolution: '720p', // Official spec indicates 720p only
      aspectRatio: '16:9 (1280x720)', // Standard aspect ratio supported
      inputTypes: ['Text-to-Video'],
      creditCost: 6.0 // Based on official pricing + margin
    }
  ];
  
  // Format options - ONLY including formats officially supported by Runware
  // Per Runware docs: Only mp4 is officially supported with 720p resolution for all models
  // Some models like Kling 2.1 support 1080p, but all support 720p
  const formatOptions: FormatOption[] = [
    { id: 'mp4-720p', name: 'MP4 720p', extension: 'mp4', quality: 80, description: 'Standard resolution (1280x720) MP4, compatible with all models' },
    { id: 'mp4-1080p', name: 'MP4 1080p', extension: 'mp4', quality: 90, description: 'Full HD resolution (1920x1080) MP4, only for models that support 1080p' }
  ];
  
  // Note: WebM options removed as they are not officially supported in Runware's documentation
  
  // Example prompts for inspiration
  const examplePrompts = [
    'A serene mountain landscape with clouds moving across the peaks at sunset',
    'A bustling city street with pedestrians walking and cars driving by',
    'Ocean waves crashing on a sandy beach with palm trees swaying in the breeze',
    'A close-up of a blooming flower with dew drops, slowly opening its petals',
    'A futuristic cityscape at night with flying vehicles moving between skyscrapers'
  ];

  // Resolution multipliers for credit calculation
  const resolutionMultipliers = {
    '720p': 1.0,  // Base multiplier for 720p
    '1080p': 1.5  // 1080p costs 1.5x more than 720p
  };

  // Calculate estimated credit cost based on model, duration, and resolution
  // Calculate required credits based on selected options using our 30% margin service
  const calculateEstimatedCredits = (modelId = selectedModel, duration = videoDuration, formatId = selectedFormat): number => {
    // Ensure we have a model selected
    if (!videoModels.length || !selectedModel) {
      return 0;
    }

    // Find the current model
    const model = videoModels.find(m => m.id === selectedModel);
    if (!model) {
      return 0;
    }

    // Get the selected format
    const selectedFormatOption = formatOptions.find(f => f.id === selectedFormat);
    if (!selectedFormatOption) {
      return 0;
    }
    
    const resolution = selectedFormatOption.id.includes('720p') ? '720p' : '1080p';
    let width = resolution === '720p' ? 1280 : 1920;
    let height = resolution === '720p' ? 720 : 1080;

    // Create a parameters object for the credit calculation service
    const params: CreditCalculationParams = {
      modelId: selectedModel,
      duration: videoDuration,
      width: resolution === '720p' ? 1280 : 1920, // Use accurate width based on selected format
      height: resolution === '720p' ? 720 : 1080, // Use accurate height based on selected format
      outputFormat: selectedFormatOption.extension.toLowerCase(),
      withAudio: selectedModel.toLowerCase().includes('sound') || selectedModel.toLowerCase().includes('audio')
    };

    // Calculate credits using our margin-inclusive service
    return calculateVideoCredits(params);
  };

  // Handle selecting a duration preset
  const handleDurationPresetChange = (preset: string) => {
    const selectedPreset = durationPresets.find(p => p.name === preset);
    if (selectedPreset) {
      // Find selected model's max duration
      const currentModel = videoModels.find(m => m.id === selectedModel);
      const maxAllowedDuration = currentModel ? currentModel.maxDuration : 5;
      
      // Ensure duration doesn't exceed model's max
      const validDuration = Math.min(selectedPreset.seconds, maxAllowedDuration);
      
      setVideoDuration(validDuration);
      setSelectedDurationPreset(preset);
      
      // If the preset exceeds the model's max, notify the user
      if (selectedPreset.seconds > maxAllowedDuration) {
        toast(`Duration limited to ${maxAllowedDuration} seconds (maximum for ${currentModel?.name})`, {
          style: { background: '#FFF3CD', color: '#856404', borderColor: '#FFEEBA' },
          duration: 4000
        });
      }
      
      // Update estimated credit cost
      updateEstimatedCreditCost(selectedModel, validDuration, selectedFormat);
    }
  };

  // Handle selecting a video model
  const handleModelChange = (modelId: string) => {
    setSelectedModel(modelId);
    
    // Get the new model details
    const newModel = videoModels.find(m => m.id === modelId);
    
    // Update estimated credit cost
    updateEstimatedCreditCost(modelId, videoDuration, selectedFormat);
    
    // Check if current duration exceeds the new model's maximum
    if (newModel && videoDuration > newModel.maxDuration) {
      // Adjust duration to match model's maximum
      setVideoDuration(newModel.maxDuration);
      
      // Find the best matching preset or clear selection if none matches
      const matchingPreset = durationPresets.find(p => p.seconds === newModel.maxDuration);
      setSelectedDurationPreset(matchingPreset ? matchingPreset.name : '');
      
      // Notify the user about the adjustment
      toast(`Duration adjusted to ${newModel.maxDuration} seconds (maximum for ${newModel.name})`, {
        style: { background: '#FFF3CD', color: '#856404', borderColor: '#FFEEBA' },
        duration: 4000
      });
    }
    
    // Check if selected format is compatible with the model's resolution
    validateAndAdjustResolution(newModel);
    
    // Add detailed log entry for model change
    if (developerMode) {
      addLog('info', `Model changed to ${modelId}`, { 
        selectedModel: newModel
      });
    }
  };
  
  // Validate and adjust resolution based on model capabilities
  const validateAndAdjustResolution = (model?: VideoModel) => {
    if (!model) return;
    
    const currentFormat = formatOptions.find(f => f.id === selectedFormat);
    if (!currentFormat) return;
    
    // Check if format resolution exceeds model capabilities
    const is1080pFormat = currentFormat.id.includes('1080p');
    const modelSupports1080p = model.maxResolution === '1080p';
    
    if (is1080pFormat && !modelSupports1080p) {
      // Switch to 720p version of the same format type - only MP4 is officially supported
      const newFormatId = 'mp4-720p';
      const newFormat = formatOptions.find(f => f.id === newFormatId);
      
      if (newFormat) {
        setSelectedFormat(newFormatId);
        
        // Update estimated credit cost with new format
        updateEstimatedCreditCost(model.id, videoDuration, newFormatId);
        
        // Notify the user about the format adjustment
        toast(`Output format adjusted to ${newFormat.name} (${model.name} only supports ${model.maxResolution})`, {
          style: { background: '#FFF3CD', color: '#856404', borderColor: '#FFEEBA' },
          duration: 4000
        });
        
        if (developerMode) {
          addLog('info', `Format automatically adjusted to ${newFormatId} for compatibility with ${model.name}`, { 
            reason: `${model.name} only supports ${model.maxResolution}`,
            previousFormat: currentFormat.id,
            newFormat: newFormatId
          });
        }
      }
    }
  };

  // Handle selecting a format option
  const handleFormatChange = (formatId: string) => {
    // Check if format is compatible with current model
    const currentModel = videoModels.find(m => m.id === selectedModel);
    const format = formatOptions.find(f => f.id === formatId);
    
    if (format && currentModel) {
      const is1080pFormat = format.id.includes('1080p');
      const modelSupports1080p = currentModel.maxResolution === '1080p';
      
      // If trying to select a 1080p format but model doesn't support it
      if (is1080pFormat && !modelSupports1080p) {
        // Force selection of 720p format
        const safeFormatId = 'mp4-720p';
        setSelectedFormat(safeFormatId);
        
        // Update estimated credit cost with safe format
        updateEstimatedCreditCost(selectedModel, videoDuration, safeFormatId);
        
        // Notify the user
        toast(`Cannot select ${format.name}: ${currentModel.name} only supports ${currentModel.maxResolution}`, {
          style: { background: '#FFF3CD', color: '#856404', borderColor: '#FFEEBA' },
          duration: 4000
        });
        
        if (developerMode) {
          addLog('warning', `Prevented selection of incompatible format ${formatId}`, {
            reason: `${currentModel.name} only supports ${currentModel.maxResolution}`,
            selectedFormatId: safeFormatId 
          });
        }
      } else {
        // Format is compatible, proceed normally
        setSelectedFormat(formatId);
        
        // Update estimated credit cost
        updateEstimatedCreditCost(selectedModel, videoDuration, formatId);
        
        if (developerMode) {
          addLog('info', `Format changed to ${formatId}`);
        }
      }
    }
  };

  // Handle using an example prompt
  const handleUseExample = (examplePrompt: string) => {
    setPrompt(examplePrompt);
  };

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);
  
  // Auto-scroll logs to bottom when new logs are added
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);
  
  // Add log entry
  const addLog = (level: LogEntry['level'], message: string, data?: any) => {
    if (!developerMode) return;
    
    const now = new Date();
    const timestamp = now.toLocaleTimeString() + '.' + now.getMilliseconds().toString().padStart(3, '0');
    
    setLogs(prevLogs => [
      ...prevLogs,
      { timestamp, level, message, data }
    ]);
  };

  // Clear logs
  const clearLogs = () => {
    setLogs([]);
    addLog('info', 'Logs cleared');
  };

  // Copy logs to clipboard
  const copyLogs = () => {
    const logText = logs.map(log => `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message} ${log.data ? JSON.stringify(log.data) : ''}`).join('\n');
    navigator.clipboard.writeText(logText);
    toast.success('Logs copied to clipboard!');
  };

  // Copy prompt to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
    addLog('info', 'Copied text to clipboard', { text });
  };

  // Toggle developer mode
  const toggleDeveloperMode = () => {
    setDeveloperMode(!developerMode);
    if (!developerMode) {
      addLog('info', 'Developer mode activated');
    }
  };

  // Helper function to update the estimated credit cost whenever relevant parameters change
  const updateEstimatedCreditCost = (modelId: string, duration: number, formatId: string) => {
    // Get the base cost per second for the selected model
    const baseCostPerSecond = getBaseModelCost(modelId);
    setBaseModelCost(baseCostPerSecond);
    
    // Calculate the total estimated cost with all options
    const cost = calculateEstimatedCredits(modelId, duration, formatId);
    setEstimatedCost(cost);
  };
  
  // Calculate required credits based on selected options using our 30% margin service
  const calculateRequiredCredits = (): number => {
    const selectedModelData = videoModels.find(m => m.id === selectedModel);
    if (!selectedModelData) return 0;
    
    // Format data
    const formatData = formatOptions.find(f => f.id === selectedFormat) || formatOptions[0];
    
    // Determine resolution based on format
    const resolution = formatData.id.includes('720p') ? '720p' : '1080p';
    const width = resolution === '720p' ? 1280 : 1920;
    const height = resolution === '720p' ? 720 : 1080;
    
    // Check if model name contains audio/sound keywords
    const hasAudio = selectedModelData.name.toLowerCase().includes('sound') || 
                     selectedModelData.name.toLowerCase().includes('audio');
    
    // Use the new credit calculation service with 30% margin
    const calculationParams = {
      modelId: selectedModel,
      duration: videoDuration,
      width: width,
      height: height,
      outputFormat: formatData.extension,
      withAudio: hasAudio
    };
    
    // Calculate using our margin-inclusive service
    return calculateVideoCredits(calculationParams);
  };

  // Check if the user has enough credits
  // Uses our margin-inclusive credit calculation to ensure accurate validation
  const hasEnoughCreditsForGeneration = (): boolean => {
    const required = calculateEstimatedCredits();
    return videoCredits >= required;
  };
  
  // Start polling for video generation status
  const pollForVideoStatus = async (taskId: string) => {
    setPollingActive(true);
    let complete = false;
    let attempts = 0;
    const maxAttempts = 120; // 10 minutes with 5-second intervals
    
    addLog('info', 'Starting status polling', { taskId });
    
    try {
      while (!complete && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds between checks
        
        // Use our service to check status
        const statusData: VideoGenerationStatus = await checkVideoGenerationStatus(taskId);
        addLog('info', 'Received status update', { statusData });
        
        // Update progress
        if (statusData.progress) {
          setGenerationProgress(statusData.progress);
        } else {
          // Estimate progress based on time elapsed
          const progressEstimate = Math.min(95, (attempts / maxAttempts) * 100);
          setGenerationProgress(progressEstimate);
        }
        
        // Update estimated time remaining if available
        if ('estimatedCompletionTime' in statusData && statusData.estimatedCompletionTime) {
          setEstimatedTimeRemaining(statusData.estimatedCompletionTime);
        }
        
        // Check if complete or failed
        if (statusData.status === 'completed') {
          setVideoUrl(statusData.videoURL || '');
          setGenerationProgress(100);
          setEstimatedTimeRemaining(null);
          complete = true;
          
          // Update credit display after generation
          if (statusData.creditCost) {
            // Refresh credit balance
            const fetchUserCredits = async () => {
              try {
                const token = await getToken();
                // getVideoCredits doesn't require a token parameter as it uses credentials: 'include'
                const credits = await getVideoCredits();
                setVideoCredits(credits);
              } catch (error) {
                console.error('Failed to refresh video credits:', error);
              }
            };
            
            fetchUserCredits();
          }
          
          addLog('success', 'Video generation completed', { videoUrl: statusData.videoURL });
          toast.success('Video generated successfully!');
        } else if (statusData.status === 'failed') {
          throw new Error(statusData.error || 'Video generation failed');
        }
        
        attempts++;
      }
      
      if (!complete) {
        throw new Error('Video generation timed out. Please check your credits page later for the result.');
      }
    } catch (error) {
      setPollingActive(false);
      setLoading(false);
      setGenerationProgress(0);
      setEstimatedTimeRemaining(null);
      addLog('error', 'Video generation failed', { error });
      toast.error(error instanceof Error ? error.message : 'Video generation failed');
    }
  };

  // Function to render the credit information section (moved from misplaced JSX outside component)
  const renderCreditInformation = () => {
    return (
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
        <div className="text-sm text-gray-500 my-2">
          <div className="flex justify-between">
            <span>Credit Cost:</span> 
            <span className="font-semibold">{calculateEstimatedCredits()} credits</span>
          </div>
          <div className="flex justify-between">
            <span>Your Balance:</span> 
            <span className="font-semibold">{videoCredits} credits</span>
          </div>
          {calculateEstimatedCredits() > videoCredits && (
            <div className="text-red-500 text-xs mt-1">
              Not enough credits. Please purchase more credits to continue.
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Render the loading/generation progress UI
  const renderGenerationProgress = () => {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Generating Video...</h3>
        <div className="w-full max-w-md bg-gray-200 rounded-full h-2.5 mb-4">
          <div 
            className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" 
            style={{ width: `${generationProgress}%` }}
          ></div>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          {estimatedTimeRemaining 
            ? `Estimated time remaining: ${Math.ceil(estimatedTimeRemaining / 60)} minutes` 
            : 'Processing your request...'
          }
        </p>
        <div className="mt-4 p-3 bg-indigo-50 rounded-lg text-sm text-indigo-700">
      </div>
    </div>
  );
  };

  // Function to render video results
  const renderVideoResults = () => {
    return (
      <div>
      {videoUrl && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Your Generated Video</h3>
          <video 
            controls 
            className="w-full rounded-lg shadow-sm" 
            src={videoUrl}
          >  
            Your browser does not support the video tag.
          </video>
          <div className="mt-4 flex gap-2">
            <a 
              href={videoUrl} 
              download="generated-video"
              className="flex-1 py-2 px-4 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded text-center text-sm font-medium transition-colors"
            >
              Download Video
            </a>
            <button 
              onClick={() => copyToClipboard(videoUrl)}
              className="py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-medium transition-colors"
            >
              Copy URL
            </button>
          </div>
        </div>
      )}
      </div>
    );
  };
  
  // Function to render the developer tools section - hidden in production
  const renderDeveloperTools = () => {
    if (ENV.NODE_ENV !== 'development') {
      return null;
    }
    
    return (
      <div className="mt-8">
        <div className="text-right">
          <button
            type="button"
            onClick={toggleDeveloperMode}
            className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            {developerMode ? 'Hide Developer Mode' : 'Developer Mode'}
          </button>
        </div>
        
        {/* Developer logs */}
        {developerMode && (
          <div className="mt-4 p-4 bg-gray-900 text-gray-100 rounded-lg shadow-inner">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-mono text-sm text-gray-300">Developer Console</h3>
              <div className="space-x-2">
                <button 
                  onClick={copyLogs} 
                  className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                >
                  Copy Logs
                </button>
                <button 
                  onClick={clearLogs} 
                  className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="overflow-auto h-64 font-mono text-xs">
              {logs.length === 0 && <div className="text-gray-500">No logs yet...</div>}
              {logs.map((log, index) => (
                <div key={index} className="mb-1">
                  <span className="text-gray-400">[{log.timestamp}]</span>
                  <span className={
                    log.level === 'error' ? ' text-red-400' :
                    log.level === 'warning' ? ' text-yellow-400' :
                    log.level === 'success' ? ' text-green-400' :
                    ' text-blue-400'
                  }>
                    [{log.level.toUpperCase()}]
                  </span>
                  <span className="ml-2">{log.message}</span>
                  {log.data && (
                    <pre className="mt-1 pl-6 text-gray-400 overflow-x-auto">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Main component render
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Text-to-Video Generator</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input column */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          {/* Credit information */}
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <div className="flex justify-between items-center text-gray-700">
              <div className="font-medium">Standard Base Cost:</div>
              <div className="font-bold">{baseModelCost} credits/second</div>
            </div>
            <div className="flex justify-between items-center text-gray-700 mt-1">
              <div className="font-medium">Total Cost (with options):</div>
              <div className="font-bold">{estimatedCost} credits</div>
            </div>
            <div className="flex justify-between items-center text-gray-700 mt-1">
              <div className="font-medium">Your Balance:</div>
              <div className="font-bold">{videoCredits} credits</div>
            </div>
            {estimatedCost > videoCredits && (
              <div className="mt-2 text-sm text-red-600">
                You need {estimatedCost - videoCredits} more credits to generate this video.
              </div>
            )}
          </div>
          
          {/* Model selection */}
          <div className="mb-6">
            <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-1">Video Model</label>
            <select
              id="model"
              value={selectedModel}
              onChange={(e) => handleModelChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading || pollingActive}
            >
              {videoModels.map(model => (
                <option key={model.id} value={model.id}>{model.name}</option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {videoModels.find(model => model.id === selectedModel)?.description || ''}
            </p>
          </div>
          
          {/* Prompt input */}
          <div className="mb-6">
            <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
            <textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 min-h-[120px]"
              placeholder="Describe the video you want to generate..."
              disabled={loading || pollingActive}
            />
          </div>
          
          {/* Duration settings */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <div>
              <select
                value={selectedDurationPreset}
                onChange={(e) => handleDurationPresetChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                disabled={loading || pollingActive}
              >
                {durationPresets.map(preset => (
                  <option key={preset.name} value={preset.name}>{preset.description}</option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Select the video length for your generation
              </p>
            </div>
          </div>
          
          {/* Format options */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
            <select
              value={selectedFormat}
              onChange={(e) => handleFormatChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading || pollingActive}
            >
              {formatOptions.map(format => {
                // Check if current model supports 1080p resolution
                const currentModel = videoModels.find(m => m.id === selectedModel);
                const supports1080p = currentModel && currentModel.maxResolution === '1080p';
                
                // Disable 1080p option for models that only support 720p
                const is1080pOption = format.id.includes('1080p');
                const isDisabled = is1080pOption && !supports1080p;
                
                return (
                  <option 
                    key={format.id} 
                    value={format.id} 
                    disabled={isDisabled}
                  >
                    {format.name}{isDisabled ? ' (not supported by this model)' : ''}
                  </option>
                );
              })}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {formatOptions.find(format => format.id === selectedFormat)?.description || ''}
            </p>
          </div>
          
          {/* Enhance prompt option */}
          <div className="mb-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enhance-prompt"
                checked={enhancePrompt}
                onChange={(e) => setEnhancePrompt(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                disabled={loading || pollingActive}
              />
              <label htmlFor="enhance-prompt" className="ml-2 block text-sm text-gray-700">
                Enhance prompt with AI assistance
              </label>
            </div>
            <p className="mt-1 text-sm text-gray-500 ml-6">
              Automatically improve your prompt to get better results
            </p>
          </div>
          
          {/* Generate button */}
          <button
            onClick={() => pollForVideoStatus(uuidv4())}
            disabled={loading || pollingActive || !prompt.trim() || !hasEnoughCreditsForGeneration()}
            className={`w-full py-3 px-4 rounded-md shadow-sm text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              loading || pollingActive || !prompt.trim() || !hasEnoughCreditsForGeneration() 
                ? 'bg-gray-300 cursor-not-allowed' 
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {loading || pollingActive ? 'Generating...' : 'Generate Video'}
          </button>
        </div>
        
        {/* Preview column */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          {loading || pollingActive ? (
            renderGenerationProgress()
          ) : videoUrl ? (
            renderVideoResults()
          ) : (
            <div className="h-full flex flex-col justify-center items-center text-center p-4">
              <div className="text-gray-400">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <p className="mt-1">Enter prompt and select options to generate a video</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {renderDeveloperTools()}
    </div>
  );
};

export default TextToVideoGenerator;
